import styles from "./EventCard.module.scss";
import classNames from "classnames";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Chip,
  LinearProgress,
  Tooltip,
} from "@mui/material";
import { format } from "date-fns";
import { cs } from "date-fns/locale";
import { useContext, useState } from "react";
import { InviteFriendsDialog } from "../InviteFriendsDialog/InviteFriendsDialog";
import { CancelEventDialog } from "../CancelEventDialog/CancelEventDialog";
import { AuthContext } from "../../context/AuthContext";
import { useEnumStore } from "../../store/enumStore";
import { EventContext } from "../../context/EventContext";

interface EventImage {
  id?: string | number;
  public_link?: string;
  url?: string;
}

interface Organiser {
  organiser_id?: string;
  organiser_picture?: string;
  name?: string;
}

interface EventCardData {
  id: string;
  event_title: string;
  event_type: string;
  event_image?: EventImage[];
  event_start_datetime: Date | string;
  location?: string;
  address?: string;
  capacity: number;
  remaining_capacity?: number | null;
  organiser?: Organiser;
  details?: string;
  my_match?: {
    id: string;
    status: string | null;
    date_of_match: string | null;
  } | null;
  match_count?: number | null;
}

interface EventCardProps {
  event: EventCardData;
  isVolunteer?: boolean;
}

export const EventCard = ({ event, isVolunteer = false }: EventCardProps) => {
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);

  if (!authContext) {
    throw new Error("AuthContext must be used within an AuthProvider");
  }

  const eventTypes = useEnumStore((state) => state.eventTypes);
  const eventContext = useContext(EventContext);

  if (!eventContext) {
    throw new Error("EventContext must be used within an EventProvider");
  }

  const { fetchEvents } = eventContext;
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

  const {
    id,
    event_title,
    event_type,
    event_image,
    event_start_datetime,
    location,
    address,
    capacity,
    remaining_capacity,
    organiser,
    details,
    my_match,
    match_count,
  } = event;

  const displayLocation = location || address || "";

  const isUserAttending = my_match?.status === "approved";
  const isCollection = event_type === "collection";
  const attendees = typeof match_count === "number" ? match_count : 0;
  const activeMatchesCount = typeof match_count === "number" ? match_count : 0;
  const isEventFull =
    typeof remaining_capacity === "number"
      ? remaining_capacity <= 0
      : capacity
        ? activeMatchesCount >= capacity
        : false;
  const capacityProgress = capacity > 0 ? (attendees / capacity) * 100 : 0;
  const eventTypeLabel =
    eventTypes?.length &&
    eventTypes?.find((item) => item.event_type === event_type)?.event_type_name;

  return (
    <>
      <Card className={styles.eventCard}>
        <CardMedia
          className={styles.image}
          image={event_image?.[0]?.public_link || event_image?.[0]?.url || ""}
          title="event-image"
        />
        <Tooltip
          title={`${capacityProgress.toFixed(0)}% naplněno`}
          placement="top"
          arrow
          slotProps={{
            popper: {
              modifiers: [
                {
                  name: "offset",
                  options: {
                    offset: [0, -4],
                  },
                },
              ],
            },
          }}
        >
          <LinearProgress
            variant="determinate"
            color={isCollection ? "secondary" : "primary"}
            value={capacityProgress}
            className={styles.progress}
          />
        </Tooltip>
        <CardContent className={styles.content}>
          <p className={styles.title}>{event_title}</p>
          <Chip
            color={isCollection ? "secondary" : "primary"}
            variant="filled"
            size="small"
            label={eventTypeLabel || "Akce"}
            className={styles.pill}
          />
          {!isCollection && event_start_datetime && (
            <>
              <p className={classNames(styles.text, styles.date)}>
                <span>
                  {format(new Date(event_start_datetime), "d. MMMM yyyy", {
                    locale: cs,
                  })}
                </span>
                , od
                <span>
                  {" "}
                  {format(new Date(event_start_datetime), "HH:mm", {
                    locale: cs,
                  })}
                </span>
              </p>
              {displayLocation && (
                <p className={styles.text}>{displayLocation}</p>
              )}
            </>
          )}
          {organiser?.organiser_picture ? (
            <img
              src={organiser.organiser_picture}
              alt="organiser-logo"
              className={styles.logo}
            />
          ) : (
            <div className={styles.templateLogo}></div>
          )}
          {details && (
            <p className={classNames(styles.text, styles.details)}>{details}</p>
          )}
        </CardContent>
        <CardActions className={styles.actions}>
          {!isVolunteer && (
            <Button
              variant="outlined"
              color="primary"
              size="small"
              fullWidth
              onClick={() => setCancelDialogOpen(true)}
            >
              Zrušit akci
            </Button>
          )}
          {isUserAttending && isVolunteer && !isEventFull && (
            <Button
              variant="outlined"
              color={isCollection ? "secondary" : "primary"}
              size="small"
              fullWidth
              onClick={() => setInviteDialogOpen(true)}
            >
              Sdílet s přáteli
            </Button>
          )}
          <Button
            variant="contained"
            color={isCollection ? "secondary" : "primary"}
            size="small"
            fullWidth
            onClick={() => navigate(isCollection ? "#" : `/event/${id}`)}
          >
            {isCollection ? "Detail sbírky" : "Detail akce"}
          </Button>
        </CardActions>
      </Card>
      {/* @ts-ignore */}
      <InviteFriendsDialog
        event={event as any}
        dialogOpen={inviteDialogOpen}
        setDialogOpen={setInviteDialogOpen}
        isCollection={isCollection}
      />
      {/* @ts-ignore */}
      <CancelEventDialog
        event={event as any}
        dialogOpen={cancelDialogOpen}
        setDialogOpen={setCancelDialogOpen}
        onSuccessCallback={() => fetchEvents()}
      />
    </>
  );
};
